/**
 * Commentaires imprimés sur le ticket du sac d'une précommande (23/10/2026).
 *
 * Demande de Philippe : « sur les tickets commande de pains, il serait bien
 * d'avoir les commentaires repris ». Le ticket du sac ne portait que le nom, le
 * numéro et le code-barres. Or les consignes ne vivent pas toutes au même
 * endroit :
 *  - « coupé » est rangé sur l'ARTICLE (`prep_options`) ; la mention « Pain
 *    coupe : … » de la note n'en est qu'un reflet (voir `notesPrecommande`) ;
 *  - la remarque du vendeur est dans la note de la précommande ;
 *  - ce que le client a écrit en commandant EN LIGNE est sur la COMMANDE liée :
 *    les 4 commentaires de commandes en ligne avec du pain sur 60 jours
 *    demandaient tous « Pouvez-vous couper les pains svp ? », et aucun
 *    n'atteignait le sac.
 * Règle pure : précommande → lignes de texte, sans doublon.
 */
import { noteLibre, estCoupeDansNote } from './notesPrecommande';

export interface PrecommandePourTicket {
  notes?: string | null;
  items?: Array<{
    quantity?: number | null;
    prep_options?: string[] | null;
    note?: string | null;
    product?: { name?: string | null } | null;
  }> | null;
  /** Commande en ligne d'où vient la précommande (pain commandé sur la boutique). */
  linked_order?: { order_number?: string | null; notes?: string | null } | null;
}

/** Note posée par le serveur sur une précommande créée depuis la boutique. */
const PHRASE_AUTO = /^pr[ée]commande automatique li[ée]e [àa] la commande\s+(\S+)/i;

/**
 * Messages que le SYSTÈME écrit à la place de la note d'une commande (paiement
 * rattrapé, montant à vérifier) : internes, ils n'ont rien à faire sur le sac.
 */
const MESSAGES_SYSTEME = [/^paiement retrouv[ée] chez stripe/i, /^montant stripe/i];

/**
 * Traces datées entre crochets que le système, ou une correction tracée, ajoute
 * aux notes : « [Retrait 20/10/2026 06:03:01] Remise en caisse (vente SALE-…) »
 * (au retrait, SOUS une ligne « --- », après le texte du client), « [Retirée —
 * statut fermé a posteriori le 10/10/2026 …] ». Vu sur staging avant la mise en
 * production : elles seraient sorties en « Remarque client ».
 */
const TRACE_DATEE = /\[[^\]]*\d{1,2}\/\d{1,2}\/\d{2,4}[^\]]*\]/g;
const LIGNE_TRACE = /^\[[^\]]*\d{1,2}\/\d{1,2}\/\d{2,4}[^\]]*\]/;
const sansTraces = (t: string) => t.replace(TRACE_DATEE, ' ').replace(/\s+/g, ' ').trim();

const plier = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export function commentairesPrecommande(p: PrecommandePourTicket): string[] {
  const lignes: string[] = [];
  const vues = new Set<string>();
  const ajouter = (texte: string) => {
    const propre = texte.replace(/\s+/g, ' ').trim();
    if (!propre || vues.has(plier(propre))) return;
    vues.add(plier(propre));
    lignes.push(propre);
  };

  // 1. Les préparations demandées, depuis les articles. Une précommande d'avant
  //    `prep_options` (aucun article n'en porte) ne les a que dans la mention de
  //    la note : on l'y relit. Jamais en complément d'articles qui en portent —
  //    deux lignes du même pain, l'une coupée, l'autre non, seraient confondues.
  const items = p.items || [];
  const avecOptions = items.some((it) => (it.prep_options || []).length > 0);
  const coupes = new Map<string, number>();
  const autres: string[] = [];
  for (const it of items) {
    const nom = (it.product?.name || '').trim();
    if (!nom) continue;
    const options = (it.prep_options || []).map((o) => o.trim()).filter(Boolean);
    const coupe = avecOptions ? options.some((o) => plier(o) === 'coupe') : estCoupeDansNote(p.notes, nom);
    if (coupe) coupes.set(nom, (coupes.get(nom) || 0) + Number(it.quantity || 1));
    const reste = options.filter((o) => plier(o) !== 'coupe');
    if (reste.length > 0) autres.push(`${nom} : ${reste.join(', ')}`);
  }
  if (coupes.size > 0) {
    ajouter(`COUPE : ${[...coupes].map(([nom, q]) => (q > 1 ? `${q} x ${nom}` : nom)).join(', ')}`);
  }
  autres.forEach(ajouter);

  // 2. La remarque libre de la précommande (sans la mention « Pain coupe »,
  //    déjà dite par la ligne COUPE). La phrase posée par le serveur pour une
  //    commande en ligne devient le numéro de cette commande : il sert à réunir
  //    au comptoir le sac du pain et celui du reste de la commande.
  for (const morceau of noteLibre(p.notes).split(/\s*\|\s*|\r?\n/)) {
    const t = sansTraces(morceau);
    if (!t) continue;
    const auto = t.match(PHRASE_AUTO);
    ajouter(auto ? `Commande en ligne ${p.linked_order?.order_number || auto[1]}` : t);
  }

  // 3. La remarque libre posée sur une ligne.
  for (const it of items) {
    const note = (it.note || '').trim();
    if (note) ajouter(`${(it.product?.name || 'Article').trim()} : ${note}`);
  }

  // 4. Ce que le client a écrit en commandant en ligne : ce qui précède la ligne
  //    « --- » sous laquelle le retrait ajoute ses traces, sans ligne de trace.
  const lignesCommande = (p.linked_order?.notes || '').split(/\r?\n/);
  const fin = lignesCommande.findIndex((l) => l.trim() === '---');
  for (const ligne of fin >= 0 ? lignesCommande.slice(0, fin) : lignesCommande) {
    const brute = ligne.trim();
    if (!brute || LIGNE_TRACE.test(brute) || MESSAGES_SYSTEME.some((re) => re.test(brute))) continue;
    const t = sansTraces(brute);
    if (t) ajouter(`Remarque client : ${t}`);
  }

  return lignes;
}

/**
 * Coupe un texte en lignes d'au plus `largeur` caractères, au mot près. Le
 * bridge n'en fait rien : l'imprimante coupait au caractère, au milieu des mots.
 * Un mot plus long que la ligne est tranché, faute de mieux.
 */
export function couperAuMot(texte: string, largeur = 40): string[] {
  const lignes: string[] = [];
  let courante = '';
  for (const mot of texte.split(/\s+/).filter(Boolean)) {
    let reste = mot;
    while (reste.length > largeur) {
      if (courante) {
        lignes.push(courante);
        courante = '';
      }
      lignes.push(reste.slice(0, largeur));
      reste = reste.slice(largeur);
    }
    if (!reste) continue;
    if (!courante) courante = reste;
    else if (courante.length + 1 + reste.length <= largeur) courante += ` ${reste}`;
    else {
      lignes.push(courante);
      courante = reste;
    }
  }
  if (courante) lignes.push(courante);
  return lignes;
}
